import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Check, CreditCard, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

const tiers = [
  { id: "starter", name: "Starter", monthly: 29, annual: 290, features: ["1 module", "5 users", "Basic analytics"] },
  { id: "professional", name: "Professional", monthly: 99, annual: 990, featured: true, features: ["3 modules", "AI Marketplace", "50 users"] },
  { id: "enterprise", name: "Enterprise", monthly: 299, annual: 2990, features: ["Unlimited", "Voice Bot", "Custom SLA"] },
];

const methods = [
  { id: "stripe", label: "Stripe", note: "Credit / Debit" },
  { id: "razorpay", label: "Razorpay", note: "UPI / Cards (India)" },
  { id: "paypal", label: "PayPal", note: "PayPal balance" },
];

export default function BillingPage() {
  const { user } = useAuth();
  const [plan, setPlan] = useState("professional");
  const [cycle, setCycle] = useState("monthly");
  const [method, setMethod] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const checkout = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      // Use the new gateway-aware endpoint. When live keys are present it
      // returns a real checkout_url; otherwise it records a mock intent.
      const { data } = await api.post(`/billing/checkout/${method}`, { plan, cycle, method });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setResult(data);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen container-px py-24" data-testid="billing-page">
      <div className="mx-auto max-w-6xl">
        <Link to="/dashboard" className="inline-flex mb-10"><Logo /></Link>
        <div className="eyebrow text-gold-300">Billing & Payments</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl text-white">Complete your subscription</h1>
        <p className="mt-3 text-white/55 max-w-2xl">
          Choose a plan, billing cycle and payment method. Payment gateway checkout is being finalized —
          your selection is saved and our team will activate it for you.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {tiers.map((t) => (
            <button
              key={t.id}
              onClick={() => setPlan(t.id)}
              data-testid={`billing-plan-${t.id}`}
              className={`text-left rounded-2xl p-6 border transition-all ${
                plan === t.id ? "border-gold-500/60 bg-gold-500/[0.08]" : "border-white/10 bg-ink-200 hover:border-white/20"
              }`}
            >
              <div className="font-serif text-xl text-white">{t.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-serif text-3xl gold-text">${cycle === "annual" ? t.annual : t.monthly}</span>
                <span className="text-xs text-white/50">/{cycle === "annual" ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-4 space-y-2">
                {t.features.map((f, i) => (
                  <li key={i} className="text-xs text-white/70 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-gold-400" /> {f}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 glass-card p-6">
            <div className="eyebrow text-gold-300">Billing cycle</div>
            <div className="mt-3 flex gap-2">
              {["monthly", "annual"].map((c) => (
                <button
                  key={c}
                  data-testid={`billing-cycle-${c}`}
                  onClick={() => setCycle(c)}
                  className={`pill text-sm ${cycle === c ? "pill-gold" : "pill-ghost"} capitalize`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-7 eyebrow text-gold-300">Payment method</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {methods.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  data-testid={`billing-method-${m.id}`}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    method === m.id ? "border-gold-500/60 bg-gold-500/[0.08]" : "border-white/10 bg-ink-200 hover:border-white/20"
                  }`}
                >
                  <CreditCard className="h-4 w-4 text-gold-300" />
                  <div className="mt-2 font-serif text-base text-white">{m.label}</div>
                  <div className="text-xs text-white/50">{m.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="glass-card p-6 h-fit">
            <div className="eyebrow text-gold-300">Order summary</div>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Plan" value={tiers.find((t) => t.id === plan).name} />
              <Row label="Cycle" value={cycle} />
              <Row label="Method" value={method} />
              <Row label="Email" value={user?.email || ""} />
            </div>
            <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
              <span className="text-sm text-white/60">Total</span>
              <span className="font-serif text-2xl gold-text">
                ${cycle === "annual" ? tiers.find((t) => t.id === plan).annual : tiers.find((t) => t.id === plan).monthly}
              </span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              data-testid="billing-checkout-btn"
              className="mt-5 w-full pill pill-gold justify-center disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Subscription"}
            </button>
            {error && <div className="mt-3 text-sm text-red-400" data-testid="billing-error">{error}</div>}
            {result && (
              <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-3 text-sm text-emerald-200" data-testid="billing-success">
                <div className="font-medium">Subscription recorded ✓</div>
                <div className="text-xs text-emerald-200/80 mt-1">{result.message}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/50">{label}</span>
      <span className="text-white capitalize">{value}</span>
    </div>
  );
}
